import { ImageView, View } from '@play-co/timestep-core/ui';
import StateObserver from 'src/StateObserver';
import LangBitmapFontTextView from 'src/lib/ui/components/LangBitmapFontTextView';
import i18n from 'src/lib/i18n/i18n';
import { createWaiterInView } from 'src/lib/utils';
import { duration } from 'src/replicant/utils/duration';
import { getJSTDateTime } from 'src/replicant/utils/time';

/** Available daily card limit types and their corresponding timer icons. */
const LIMIT_ICON_IMAGES = {
  send: 'assets/ui/cards/common/icon_send_card.png',
  receive: 'assets/ui/cards/common/icon_receive_card.png',
  trade: 'assets/ui/cards/common/icon_trade.png',
} as const;

/** Card limit type represented by the daily reset timer. */
export type CardsDailyLimitKind = keyof typeof LIMIT_ICON_IMAGES;

/**
 * Displays the remaining time until the current JST card-limit day resets.
 */
export default class CardsDailyLimitTimer extends View {
  private icon: ImageView;
  private timerText: LangBitmapFontTextView;
  private waiter;

  /**
   * Create a hidden daily-limit timer at the requested position.
   *
   * @param opts Timer parent and position within that parent.
   */
  constructor(opts: { superview: View; x: number; y: number }) {
    super({
      superview: opts.superview,
      width: 215,
      height: 52,
      x: opts.x,
      y: opts.y,
      centerOnOrigin: true,
      visible: false,
    });

    new ImageView({
      superview: this,
      image: 'assets/ui/cards/common/timer_bg.png',
      width: this.style.width,
      height: this.style.height,
    });

    this.icon = new ImageView({
      superview: this,
      image: LIMIT_ICON_IMAGES.receive,
      width: 66,
      height: 68,
      x: 40,
      y: this.style.height * 0.5,
      centerOnOrigin: true,
    });

    this.timerText = new LangBitmapFontTextView({
      superview: this,
      width: 100,
      height: 52,
      x: this.style.width * 0.5 + 20,
      y: this.style.height * 0.5,
      centerOnOrigin: true,
      font: 'Body',
      size: 26,
      align: 'center',
      verticalAlign: 'center',
      color: '#E7291C',
      localeText: () => '00:00',
    });

    this.waiter = createWaiterInView(this);
  }

  /**
   * Show the requested limit type and start its JST daily reset countdown.
   *
   * @param limitKind Card limit represented by the timer icon.
   * @param onExpire Optional callback invoked when the daily limit resets.
   */
  public start(limitKind: CardsDailyLimitKind, onExpire?: VoidFunction) {
    this.icon.updateOpts({ image: LIMIT_ICON_IMAGES[limitKind] });
    this.show();

    const expiresAt = getJSTDateTime(StateObserver.now()).set({
      hour: 23,
      minute: 59,
      second: 59,
    });
    this.updateCountdown(expiresAt.valueOf(), onExpire);
  }

  /**
   * Refresh the displayed countdown until the daily limit resets.
   *
   * @param expiresAt Timestamp when the current JST limit day ends.
   * @param onExpire Optional callback invoked after the reset time.
   */
  private updateCountdown(expiresAt: number, onExpire?: VoidFunction) {
    const timeRemaining = expiresAt - StateObserver.now();
    if (timeRemaining < 0) {
      onExpire?.();
      return;
    }

    const h = Math.floor(timeRemaining / duration({ hours: 1 }));
    const m = Math.floor(timeRemaining / duration({ minutes: 1 })) % 60;
    this.timerText.localeText = () => i18n('cards.limitTimer', { h, m });

    this.waiter.wait(
      () => this.updateCountdown(expiresAt, onExpire),
      duration({ minutes: 1 }),
    );
  }
}
